/**
 * Session-based caching to reduce API calls
 * Uses sessionStorage which persists only for the current browser session
 * Clears when the tab/browser is closed
 */

const CACHE_PREFIX = 'hrportal_cache_';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes TTL for cached data

export const sessionCache = {
    /**
     * Get cached data if it's still valid
     */
    get(key) {
        try {
            const cacheKey = CACHE_PREFIX + key;
            const cached = sessionStorage.getItem(cacheKey);

            if (!cached) return null;

            const { data, timestamp } = JSON.parse(cached);
            const now = Date.now();

            // Check if cache is expired
            if (now - timestamp > CACHE_TTL) {
                sessionStorage.removeItem(cacheKey);
                return null;
            }

            return data;
        } catch (error) {
            console.error('Cache read error:', error);
            return null;
        }
    },

    /**
     * Store data in cache
     */
    set(key, data) {
        try {
            const cacheKey = CACHE_PREFIX + key;
            const cacheData = {
                data,
                timestamp: Date.now()
            };

            sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
        } catch (error) {
            console.error('Cache write error:', error);
            // If storage is full, clear old cache entries
            if (error.name === 'QuotaExceededError') {
                this.clearOldEntries();
            }
        }
    },

    /**
     * Clear specific cache entry
     */
    clear(key) {
        const cacheKey = CACHE_PREFIX + key;
        sessionStorage.removeItem(cacheKey);
    },

    /**
     * Clear all cache entries
     */
    clearAll() {
        const keys = Object.keys(sessionStorage);
        keys.forEach(key => {
            if (key.startsWith(CACHE_PREFIX)) {
                sessionStorage.removeItem(key);
            }
        });
    },

    /**
     * Clear expired entries
     */
    clearOldEntries() {
        const keys = Object.keys(sessionStorage);
        const now = Date.now();

        keys.forEach(key => {
            if (key.startsWith(CACHE_PREFIX)) {
                try {
                    const cached = sessionStorage.getItem(key);
                    const { timestamp } = JSON.parse(cached);

                    if (now - timestamp > CACHE_TTL) {
                        sessionStorage.removeItem(key);
                    }
                } catch (error) {
                    // Remove corrupted entries
                    sessionStorage.removeItem(key);
                }
            }
        });
    },

    /**
     * Check if cache exists and is valid
     */
    has(key) {
        return this.get(key) !== null;
    }
};

// Clear old entries on load
sessionCache.clearOldEntries();